import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowUpRight,
  Lock,
  Download,
  FileText,
  Image as ImageIcon,
  Terminal,
  Globe,
  Sparkles,
  Package,
} from 'lucide-react';

// -------- Meta swap --------
const LINKS_META = {
  title: 'Links · every URL on themobilitygroup.co',
  description:
    'Internal directory of every public page, gated view, downloadable asset and API endpoint on The Mobility Group.',
};

const useLinksMeta = () => {
  useEffect(() => {
    const originalTitle = document.title;
    document.title = LINKS_META.title;
    return () => {
      document.title = originalTitle;
    };
  }, []);
};

// -------- Data --------
const SITE_ROOT = 'https://themobilitygroup.co';

const GROUPS = [
  {
    title: 'Main pages',
    icon: Globe,
    items: [
      ['/', 'Home', 'The technology, consulting & investment group for automotive & mobility'],
      ['/platforms', 'Platforms', '8 operated + backed automotive platforms'],
      ['/solutions', 'Solutions', 'What we ship for OEMs, dealers, financiers, insurers'],
      ['/investments', 'Investments', 'Ventures we operate and back'],
      ['/landscape', 'Landscape', 'India mobility landscape · FY26 numbers'],
      ['/use-cases', 'Use Cases', 'Named partner outcomes'],
      ['/about', 'About', 'Who runs TMG'],
      ['/contact', 'Contact', 'Public contact form → lands in /leads'],
      ['/insights/two-wheeler-dealerships', 'Insight · 2W', '2-wheeler OEM intelligence blueprint'],
      ['/brand', 'Brand logos', 'Logo concept explorations'],
    ],
  },
  {
    title: 'Concept pitches',
    icon: Sparkles,
    items: [
      ['/vidaproject', 'VIDA · pitch page', 'PULSE OS concept for Hero MotoCorp VIDA (public)'],
      ['/vidaproject/demo', 'VIDA · live demo', 'Interactive HQ console, journey replay, tour'],
      ['/projects/SIAM', 'SIAM × TMG · pitch', 'Gated · passcode SIAM2026 · concept for SIAM leadership', 'gated'],
    ],
  },
  {
    title: 'Internal / gated views',
    icon: Lock,
    items: [
      ['/leads', 'Leads dashboard', 'All contact + Atlas leads · passcode TMG2026', 'gated'],
      ['/admin/leads', 'Leads dashboard · alias', 'Same as /leads', 'gated'],
      ['/investments/future-bets', 'Future bets pipeline', 'Scouting pipeline · passcode TMG2026 · anyone can submit a prospect', 'gated'],
      ['/charter', 'TMG Charter · network agreement', 'Members-only charter · passcode TMG2026 · non-binding founding document', 'gated'],
    ],
  },
  {
    title: 'Direct downloads',
    icon: Download,
    items: [
      ['/tmg-brand-assets.zip', 'Brand pack (ZIP)', 'All lockups + monogram · PNG + JPG + SVG + README · ~200 KB', 'download'],
      ['/api/projects/siam/pdf', 'SIAM × TMG PDF', 'Concept pitch · 7 pages · A4 landscape · ungated'],
      ['/api/atlas/download', 'India Mobility Atlas PDF', 'Gated (name + email + company + role) via /landscape UI'],
    ],
  },
  {
    title: 'Brand assets',
    icon: ImageIcon,
    items: [
      // Star mark (finalised) — square, for LinkedIn / avatars / favicons
      ['/brand/tmg-star-linkedin-dark-1024.png', 'Star · LinkedIn · amber on black · 1024', 'PNG · square · LinkedIn-safe area', 'download'],
      ['/brand/tmg-star-linkedin-dark-400.png', 'Star · LinkedIn · amber on black · 400', 'PNG · square · LinkedIn minimum', 'download'],
      ['/brand/tmg-star-linkedin-white-1024.png', 'Star · amber on white · 1024', 'PNG · square', 'download'],
      ['/brand/tmg-star-linkedin-transparent-1024.png', 'Star · amber transparent · 1024', 'PNG · any background', 'download'],
      ['/brand/tmg-star-linkedin-white-mark-1024.png', 'Star · white on black · 1024', 'PNG · reversed', 'download'],
      ['/brand/tmg-star-dark.svg', 'Star · amber · SVG', 'Vector · scales to any size', 'download'],
      ['/brand/tmg-star-transparent.svg', 'Star · transparent · SVG', 'Vector · scales to any size', 'download'],
      ['/brand/tmg-star-white.svg', 'Star · white · SVG', 'Vector · scales to any size', 'download'],
      ['/brand/tmg-star-amber.pdf', 'Star · amber · PDF', 'Print · vector', 'download'],
      ['/brand/tmg-star-white.pdf', 'Star · white · PDF', 'Print · vector', 'download'],
      // LinkedIn banner options
      ['/brand/tmg-linkedin-banner-01-constellation.png', 'LinkedIn banner · Constellation', '1128×191 · network mesh', 'download'],
      ['/brand/tmg-linkedin-banner-02-grid-pulse.png', 'LinkedIn banner · Data grid · KPIs', '1128×191 · dot grid + FY26 stats', 'download'],
      ['/brand/tmg-linkedin-banner-03-perspective.png', 'LinkedIn banner · Motion perspective', '1128×191 · converging lines', 'download'],
      ['/brand/tmg-linkedin-banner-04-ghost-kpis.png', 'LinkedIn banner · Ghost KPIs', '1128×191 · translucent stats', 'download'],
      ['/brand/tmg-linkedin-banner-05-signal.png', 'LinkedIn banner · Signal wave', '1128×191 · amber sine wave', 'download'],
      // Horizontal lockups — PNG (source rasters, transparent alpha preserved)
      ['/tmg-lockup-horizontal-dark.png', 'Lockup · dark · PNG', 'On black · white text · 1200×300', 'download'],
      ['/tmg-lockup-horizontal-light.png', 'Lockup · light · PNG', 'On cream · dark text · 1200×300', 'download'],
      ['/tmg-lockup-horizontal-transparent-white.png', 'Lockup · transparent white · PNG', 'Any dark background · 1200×300', 'download'],
      ['/tmg-lockup-horizontal-transparent-dark.png', 'Lockup · transparent dark · PNG', 'Any light background · 1200×300', 'download'],
      // Horizontal lockups — SVG source (scale to any size, editable)
      ['/brand/tmg-lockup-horizontal-dark.svg', 'Lockup · dark · SVG', 'Editable vector · scales to any size', 'download'],
      ['/brand/tmg-lockup-horizontal-light.svg', 'Lockup · light · SVG', 'Editable vector · scales to any size', 'download'],
      ['/brand/tmg-lockup-horizontal-transparent-white.svg', 'Lockup · transparent white · SVG', 'Editable vector · any dark bg', 'download'],
      ['/brand/tmg-lockup-horizontal-transparent-dark.svg', 'Lockup · transparent dark · SVG', 'Editable vector · any light bg', 'download'],
      // Horizontal lockups — JPG (for docs/decks that dislike PNG alpha)
      ['/brand/tmg-lockup-horizontal-dark.jpg', 'Lockup · dark · JPG', 'For MS Word / Google Docs · 1200×300', 'download'],
      ['/brand/tmg-lockup-horizontal-light.jpg', 'Lockup · light · JPG', 'For MS Word / Google Docs · 1200×300', 'download'],
      // Monogram legacy
      ['/brand/tmg-monogram-dark-1024.png', 'Monogram · dark · 1024', 'Legacy TMG-box square mark', 'download'],
      ['/brand/tmg-monogram-light-1024.png', 'Monogram · light · 1024', 'Legacy TMG-box · on cream', 'download'],
      // OG previews
      ['/og-image.png', 'OG image · main', 'WhatsApp / LinkedIn / Twitter preview for the site'],
      ['/og-vida.png', 'OG image · VIDA', 'Per-page share preview for /vidaproject'],
    ],
  },
  {
    title: 'API endpoints',
    icon: Terminal,
    items: [
      ['/api/', 'API root', 'Health check · GET'],
      ['/api/contact', 'Contact submit', 'POST · lands in /leads'],
      ['/api/atlas/download', 'Atlas PDF', 'POST with lead → returns PDF binary'],
      ['/api/projects/siam/pdf', 'SIAM PDF', 'GET · direct download'],
      ['/api/admin/leads', 'All leads (JSON)', 'GET · requires X-Admin-Token header', 'gated'],
      ['/api/internal/prospects', 'Prospects (JSON)', 'GET · requires X-Admin-Token header', 'gated'],
      ['/api/internal/prospects/submit', 'Submit a prospect', 'POST · public (anyone can suggest a bet)'],
    ],
  },
  {
    title: 'SEO & metadata',
    icon: FileText,
    items: [
      ['/sitemap.xml', 'Sitemap', 'Public URLs for Google / Bing'],
      ['/robots.txt', 'Robots', 'Crawler rules · disallows admin, leads, projects'],
    ],
  },
];

// -------- Page --------
const Links = () => {
  useLinksMeta();
  return (
    <div className="min-h-screen bg-black text-white">
      {/* Sticky top bar */}
      <div className="sticky top-0 z-40 border-b border-white/10 bg-black/80 backdrop-blur-xl">
        <div className="px-6 md:px-12 lg:px-16 h-14 flex items-center justify-between">
          <Link
            to="/"
            data-testid="links-back"
            className="font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-400 hover:text-amber inline-flex items-center gap-2"
          >
            ← themobilitygroup.co
          </Link>
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 hidden sm:inline">
            Directory · every URL
          </span>
        </div>
      </div>

      {/* Header */}
      <section className="px-6 md:px-12 lg:px-16 pt-16 md:pt-24 pb-12 md:pb-16 border-b border-white/10">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber mb-3">
          Directory
        </div>
        <h1 className="font-display text-[40px] sm:text-5xl md:text-6xl lg:text-7xl text-balance max-w-4xl leading-[1.02]">
          Every URL, in one place.
        </h1>
        <p className="mt-6 max-w-3xl text-base md:text-lg text-zinc-300 leading-relaxed">
          Every page, gated view, downloadable asset, brand file, API endpoint
          and metadata file on themobilitygroup.co — grouped, described, and
          linked. Bookmark this and share with your team.
        </p>
      </section>

      {/* Brand pack — one-click ZIP */}
      <section className="px-6 md:px-12 lg:px-16 py-12 md:py-16 border-b border-white/10 bg-ink-900/30">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3 mb-3">
              <Package className="w-4 h-4 text-amber" />
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber">
                Brand pack · v1
              </span>
            </div>
            <h2 className="font-display text-3xl md:text-4xl text-white leading-tight">
              Download the full brand kit.
            </h2>
            <p className="mt-3 text-zinc-400 text-sm md:text-base leading-relaxed max-w-2xl">
              Four horizontal lockups (PNG, JPG, editable SVG) and the square
              TMG monogram (SVG + PNG at 512 & 1024) in a single zip, with a
              short usage README. Roughly 200 KB.
            </p>
          </div>
          <div className="lg:col-span-4 flex flex-wrap gap-3 lg:justify-end">
            <a
              href="/tmg-brand-assets.zip"
              download=""
              data-testid="download-brand-zip"
              className="inline-flex items-center gap-2 border border-amber bg-amber px-6 py-3 text-xs font-mono uppercase tracking-[0.18em] text-black hover:bg-white hover:border-white transition-colors"
            >
              <Download className="w-3.5 h-3.5" />
              Download ZIP
            </a>
            <Link
              to="/brand"
              data-testid="view-brand-page"
              className="inline-flex items-center gap-2 border border-white/25 px-6 py-3 text-xs font-mono uppercase tracking-[0.18em] text-white hover:border-white hover:bg-white/5 transition-colors"
            >
              View concepts
            </Link>
          </div>
        </div>
      </section>

      {/* Groups */}
      {GROUPS.map((group) => {
        const GroupIcon = group.icon;
        return (
          <section
            key={group.title}
            className="px-6 md:px-12 lg:px-16 py-12 md:py-16 border-b border-white/10"
          >
            <div className="flex items-center gap-4 mb-8">
              <div className="grid h-10 w-10 place-items-center border border-amber/40 bg-amber/10">
                <GroupIcon className="w-4 h-4 text-amber" />
              </div>
              <h2 className="font-display text-2xl md:text-3xl text-white">
                {group.title}
              </h2>
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 ml-auto">
                {group.items.length} link{group.items.length > 1 ? 's' : ''}
              </span>
            </div>

            <div className="grid gap-px bg-white/5 border border-white/10">
              {group.items.map(([path, label, desc, tag]) => {
                const isApi = path.startsWith('/api');
                const isFile = /\.(png|jpg|jpeg|pdf|xml|txt|svg|zip)$/i.test(path);
                const external = isApi || isFile;
                const shouldDownload = tag === 'download';
                const commonClass =
                  'bg-ink-900 hover:bg-ink-900/60 hover:border-amber transition-colors block p-5 md:p-6 group';
                const body = (
                  <>
                    <div className="flex flex-wrap items-baseline justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="font-display text-lg md:text-xl text-white group-hover:text-amber transition-colors">
                          {label}
                        </div>
                        <div className="mt-1 text-sm text-zinc-400 leading-snug">
                          {desc}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {tag === 'gated' && (
                          <span className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.18em] border border-amber/40 text-amber px-2 py-0.5">
                            <Lock className="w-2.5 h-2.5" /> gated
                          </span>
                        )}
                        {tag === 'download' && (
                          <span className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.18em] border border-amber/40 text-amber px-2 py-0.5">
                            <Download className="w-2.5 h-2.5" /> download
                          </span>
                        )}
                        {tag === 'download' ? (
                          <Download className="w-4 h-4 text-zinc-500 group-hover:text-amber transition-colors" />
                        ) : (
                          <ArrowUpRight className="w-4 h-4 text-zinc-500 group-hover:text-amber transition-colors" />
                        )}
                      </div>
                    </div>
                    <div className="mt-3 font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-500 break-all">
                      {SITE_ROOT}
                      <span className="text-amber">{path}</span>
                    </div>
                  </>
                );
                return external ? (
                  <a
                    key={path}
                    href={path}
                    {...(shouldDownload
                      ? { download: '' }
                      : { target: '_blank', rel: 'noopener noreferrer' })}
                    data-testid={`link-${path.replace(/[^a-zA-Z0-9]+/g, '-')}`}
                    className={commonClass}
                  >
                    {body}
                  </a>
                ) : (
                  <Link
                    key={path}
                    to={path}
                    data-testid={`link-${path.replace(/[^a-zA-Z0-9]+/g, '-')}`}
                    className={commonClass}
                  >
                    {body}
                  </Link>
                );
              })}
            </div>
          </section>
        );
      })}

      {/* Footer */}
      <section className="px-6 md:px-12 lg:px-16 py-12 md:py-16">
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 leading-relaxed max-w-4xl">
          Internal directory · themobilitygroup.co / links
        </p>
        <p className="mt-4 text-xs text-zinc-500 leading-relaxed max-w-3xl">
          Passcodes are not printed here. Gated pages show a passcode prompt on
          arrival. Ask the TMG team for access.
        </p>
      </section>
    </div>
  );
};

export default Links;
